<template>
  <div class="pb-6">
    <v-card class="pa-4 mb-4">
      <div class="d-flex flex-wrap align-center gap-4">
        <select-filter v-model="selectedBudgetIcon" :items="budgetIconItems" :style="{ maxWidth: selectedBudgetIcon == 'xpf' ? '105px' : '74px' }" style="flex: 1 0 74px">
          <template #selection="{ item: { raw: item } }">
            <i :class="`budget-${item.value}`"></i>
          </template>
          <template #item="{ props, item: { raw: item } }">
            <v-list-item v-bind="props">
              <template #title>
                <i :class="`budget-${item.value}`" class="mr-2"></i><span v-if="item.value != 'xpf'">{{ item.title }}</span>
              </template>
            </v-list-item>
          </template>
        </select-filter>

        <div class="d-flex flex-wrap align-center gap-4 flex-grow-1 line-height-comfortable">
          <div v-if="budgetTarget">
            <b>{{ $t('PROJECT_SHEET.TARGET_BUDGET') }}</b> : {{ $filters.number(budgetTarget) }}
          </div>
          <div>
            <b class="text-capitalize">{{ $t('PROJECT_SHEET.BUDGET_SPENT') }}</b> : {{ $filters.number(selectedBudgetData.total) }}
          </div>
          <div v-if="budgetTarget" :class="selectedBudgetData.remaining >= 0 ? 'bg-successgreen' : 'bg-errorred'" class="px-3 py-1 text-capitalize rounded-sm">
            {{ $t('PROJECT_SHEET.BUDGET_REMAINING') }} : {{ $filters.number(selectedBudgetData.remaining) }}
          </div>
        </div>
      </div>

      <div v-if="selectedBudgetData.states.length" class="d-flex align-center mt-4">
        <div class="rounded-sm overflow-hidden d-flex flex-grow-1" style="height: 20px; column-gap: 2px">
          <div v-for="state in selectedBudgetData.states" :key="state.key" :title="`${state.label} : ${$filters.number(state.amount)}`"
               :style="{ backgroundColor: `${$vuetify.theme.current.colors[state.color]}66`, width: `${state.percent}%` }" class="flex-center-center">
            <span v-if="state.percent > 4.5" class="small line-height-normal">{{ Math.round(state.percent) }}%</span>
          </div>
          <div v-if="selectedBudgetData.remainingPercent" :title="`${$t('PROJECT_SHEET.BUDGET_REMAINING')} : ${$filters.number(selectedBudgetData.remaining)}`"
               :style="{ backgroundColor: `${$vuetify.theme.current.colors.lightgrey}cc`, width: `${selectedBudgetData.remainingPercent}%` }" class="flex-center-center">
            <span v-if="selectedBudgetData.remainingPercent > 4.5" class="small line-height-normal">{{ Math.round(selectedBudgetData.remainingPercent) }}%</span>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Budgets Summary -->
    <div class="mt-8 mb-2 font-weight-bold">{{ $t('PROJECT_SHEET.BUDGET_SUMMARY') }}</div>
    <v-card>
      <div v-if="sortedBudgetIcons.length" class="table-responsive">
        <table class="table text-center">
          <thead class="bg-lightgrey">
            <tr>
              <th class="text-left pl-6 nobr">
                {{ $t('MANAGE.CONFIG.BUDGET_STATE') }}
              </th>
              <template v-for="icon in sortedBudgetIcons" :key="icon">
                <th class="nobr">
                  {{ $t('ELEMENT-DETAIL.AMOUNT_INPROGRESS') }} <i :class="`budget-${icon}`"></i>
                </th>
                <th class="nobr">
                  {{ $t('ELEMENT-DETAIL.AMOUNT') }} <i :class="`budget-${icon}`"></i>
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-for="state in summaryBudgetStateKeys" :key="state">
              <td class="text-left pl-6">
                <div class="d-inline-flex align-center">
                  <span class="mr-2 rounded-circle" :class="`bg-${selectedBudgetStatesColors[state]}`" style="width: 10px; height: 10px; opacity: 0.6">
                  </span>
                  <span>{{ budgetStates[state] || '-' }}</span>
                </div>
              </td>
              <template v-for="icon in sortedBudgetIcons" :key="`${state}-${icon}`">
                <td>
                  <template v-if="sumBudgetByIconsStates[icon][state]?.amount_inprogress">{{ $filters.number(sumBudgetByIconsStates[icon][state].amount_inprogress) }}</template>
                </td>
                <td>
                  <template v-if="sumBudgetByIconsStates[icon][state]?.amount">{{ $filters.number(sumBudgetByIconsStates[icon][state].amount) }}</template>
                </td>
              </template>
            </tr>
            <tr class="text-primary strong">
              <td class="text-left pl-6">{{ $t('GLOBAL.TOTAL') }}</td>
              <template v-for="icon in sortedBudgetIcons" :key="`total-${icon}`">
                <td class="nobr">
                  <template v-if="sumBudgetByIconsStates[icon].totals.totalAmountInProgress">{{ $filters.number(sumBudgetByIconsStates[icon].totals.totalAmountInProgress) }}</template>
                </td>
                <td class="nobr">
                  <template v-if="sumBudgetByIconsStates[icon].totals.totalAmount">{{ $filters.number(sumBudgetByIconsStates[icon].totals.totalAmount) }}</template>
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="pa-6 text-medium-emphasis">{{ $t('PROJECT_SHEET.NO_BUDGET') }}</div>
    </v-card>

    <!-- Budgets details by bubble -->
    <div class="mt-8 mb-2 font-weight-bold">{{ $t('PROJECT_SHEET.BUDGET_DETAIL_BY_BUBBLE') }}</div>
    <div class="d-flex gap-2 mb-2">
      <text-filter v-model="detailFilters.title" :label="$t('PROJECT_SHEET.FILTERS.ELEMENT_TITLE')" clearable prepend-inner-icon="far fa-search" style="flex: 1 1 30%">
      </text-filter>
      <div class="mx-2 my-1"></div>
      <select-filter v-model="detailFilters.states" :items="budgetStateFilterItems" :label="$t('MANAGE.CONFIG.BUDGET_STATE')"
                     prepend-inner-icon="far fa-coins" multiple clearable style="flex: 1 1 30%">
        <template #selection="{ item: { raw: item }, index }">
          <span v-if="detailFilters.states.length == 1">
            {{ item.title }}
          </span>
          <span v-else-if="index === 0">{{ detailFilters.states.length }}/{{ budgetStateFilterItems.length }}</span>
        </template>
      </select-filter>
    </div>
    <v-card>
      <div v-if="detailBudgetRows.length" class="table-responsive">
        <table class="table text-center">
          <thead class="bg-lightgrey">
            <tr>
              <th class="text-left pl-6 nobr">{{ $t('GLOBAL.BUBBLE') }}</th>
              <template v-for="icon in sortedBudgetIcons" :key="`detail-head-${icon}`">
                <th class="nobr">
                  {{ $t('ELEMENT-DETAIL.AMOUNT_INPROGRESS') }} <i :class="`budget-${icon}`"></i>
                </th>
                <th class="nobr">
                  {{ $t('ELEMENT-DETAIL.AMOUNT') }} <i :class="`budget-${icon}`"></i>
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in detailBudgetRows" :key="row.key">
              <td class="text-left pl-6 nobr small">
                <span style="font-style: italic;" class="pointer" @click="$emit('open-element', row.el)">{{ row.title }}</span>
                <span v-if="budgetStates[row.state]" class="small">&nbsp;({{ $filters.lowercase(budgetStates[row.state]) }})</span>
              </td>
              <template v-for="icon in sortedBudgetIcons" :key="`${row.key}-${icon}`">
                <td class="small nobr text-center">
                  <span v-if="row.budgetsByIcon[icon]?.amount_inprogress">{{ $filters.number(row.budgetsByIcon[icon].amount_inprogress) }}</span>
                </td>
                <td class="small nobr text-center">
                  <span v-if="row.budgetsByIcon[icon]?.amount">{{ $filters.number(row.budgetsByIcon[icon].amount) }}</span>
                </td>
              </template>
            </tr>
            <tr class="text-primary strong">
              <td class="text-left pl-6 nobr">{{ $t('GLOBAL.TOTAL') }}</td>
              <template v-for="icon in sortedBudgetIcons" :key="`detail-total-${icon}`">
                <td class="nobr text-center">
                  <template v-if="detailBudgetTotalsByIcon[icon]?.amount_inprogress">{{ $filters.number(detailBudgetTotalsByIcon[icon].amount_inprogress) }}</template>
                </td>
                <td class="nobr text-center">
                  <template v-if="detailBudgetTotalsByIcon[icon]?.amount">{{ $filters.number(detailBudgetTotalsByIcon[icon].amount) }}</template>
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="pa-6 text-medium-emphasis">{{ $t('PROJECT_SHEET.NO_BUDGET') }}</div>
    </v-card>
  </div>
</template>

<script>
  import useUsersAccessRightStore from '@/stores/users/accessRight';

  const budgetSummaryPalette = ['primary', 'success', 'error', 'info', 'warning', 'midgrey'];

  export default {
    props: {
      planning: { type: Object, required: true },
    },
    emits: ['open-element'],
    data() {
      return {
        selectedBudgetIcon: '',
        detailFilters: {
          title: '',
          states: [],
        },
        budgetIcons: [
          { title: 'EUR', value: '' },
          { title: 'USD', value: 'usd' },
          { title: 'GBP', value: 'gbp' },
          { title: 'F CFP', value: 'xpf' },
          { title: this.$t('ELEMENT-DETAIL.UNIT'), value: 'unit' },
        ],
        usersAccessRightStore: useUsersAccessRightStore(),
      };
    },
    computed: {
      budgetStates() {
        return this.usersAccessRightStore.config.budgetStates;
      },
      budgetStateKeys() {
        return [...Object.keys(this.budgetStates), ''];
      },
      budgetStateKeysSet() {
        return new Set(this.budgetStateKeys);
      },
      sortedBudgetIcons() {
        return Object.keys(this.sumBudgetByIconsStates).sort((iconA, iconB) => {
          const totalA = this.sumBudgetByIconsStates[iconA].totals.totalAmount;
          const totalB = this.sumBudgetByIconsStates[iconB].totals.totalAmount;
          if (totalA === totalB) {
            return this.sumBudgetByIconsStates[iconB].totals.totalAmountInProgress - this.sumBudgetByIconsStates[iconA].totals.totalAmountInProgress;
          }
          return totalB - totalA;
        });
      },
      budgetIconItems() {
        if (! this.sortedBudgetIcons.length) return this.budgetIcons;
        return this.budgetIcons.filter(icon => this.sortedBudgetIcons.includes(icon.value));
      },
      summaryBudgetStateKeys() {
        return this.budgetStateKeys.filter(state => this.sortedBudgetIcons.some(
          icon => this.sumBudgetByIconsStates[icon][state]?.amount_inprogress || this.sumBudgetByIconsStates[icon][state]?.amount,
        ));
      },
      filteredBudgetElements() {
        return (this.planning.elements || []).filter((el) => {
          if (el.isType('macro')) return false;
          const elBudgets = el.getBudgets();
          if (! elBudgets) return false;
          if (! elBudgets.some(budget => budget.amount || budget.amount_inprogress)) return false;
          return true;
        }).sort((a, b) => (a.getLaneId() < b.getLaneId() ? -1 : 1));
      },
      sumBudgetByIconsStates() {
        // compute el budgets to organize as { [icon]: { [state] : { amont, amount_inprogress}, totals: { totalAmount, totalAmountInProgress } }}
        const budgetsByIconsStates = {};
        this.filteredBudgetElements.forEach((el) => {
          (el.getBudgets() || []).forEach((budget) => {
            const state = this.budgetStateKeysSet.has(budget.state) ? budget.state : '';
            const icon = budget.icon || '';
            if (! budgetsByIconsStates[icon]) budgetsByIconsStates[icon] = { totals: { totalAmount: 0, totalAmountInProgress: 0 } };
            if (! budgetsByIconsStates[icon][state]) budgetsByIconsStates[icon][state] = { amount: 0, amount_inprogress: 0 };
            budgetsByIconsStates[icon][state].amount += budget.amount || 0;
            budgetsByIconsStates[icon][state].amount_inprogress += budget.amount_inprogress || 0;
            budgetsByIconsStates[icon].totals.totalAmount += budget.amount || 0;
            budgetsByIconsStates[icon].totals.totalAmountInProgress += budget.amount_inprogress || 0;
          });
        });
        return budgetsByIconsStates;
      },
      selectedBudgetData() {
        // data for top summary budget for selected icon
        const budgetByStates = this.sumBudgetByIconsStates[this.selectedBudgetIcon] || {};
        const states = this.budgetStateKeys.reduce((acc, key) => {
          const stateBudget = budgetByStates[key];
          const amount = Math.max(stateBudget?.amount || 0, stateBudget?.amount_inprogress || 0);
          if (! amount) return acc;
          acc.push({
            key,
            label: this.budgetStates[key] || '-',
            amount,
            color: budgetSummaryPalette[Math.min(acc.length, budgetSummaryPalette.length - 1)],
          });
          return acc;
        }, []);

        const total = states.reduce((sum, state) => sum + state.amount, 0);
        const remaining = this.budgetTarget ? this.budgetTarget - total : null;
        const baseAmount = this.budgetTarget ? Math.max(this.budgetTarget, total) : total;
        const remainingPercent = (this.budgetTarget && total < this.budgetTarget) ? (this.budgetTarget - total) / baseAmount * 100 : 0;

        return {
          total,
          remaining,
          remainingPercent,
          states: states.map(state => ({ ...state, percent: baseAmount ? state.amount / baseAmount * 100 : 0 })),
        };
      },
      selectedBudgetStatesColors() {
        // report top summary budget colors for table by state
        return this.budgetStateKeys.reduce((acc, state) => {
          const stateData = this.selectedBudgetData.states.find(item => item.key === state);
          acc[state] = stateData ? stateData.color : 'transparent'; // may not exist if no budget for this state for selected icon
          return acc;
        }, {});
      },
      budgetStateFilterItems() {
        return this.budgetStateKeys.map(state => ({
          value: state,
          title: this.budgetStates[state] || '-',
        }));
      },
      elBudgetsByStatesByEl() {
        return this.filteredBudgetElements.reduce((acc, el) => {
          const budgetsByStates = {};
          (el.getBudgets() || []).forEach((budget) => {
            if (! budget.amount && ! budget.amount_inprogress) return;
            const state = this.budgetStateKeysSet.has(budget.state) ? budget.state : '';
            const icon = budget.icon || '';
            if (! budgetsByStates[state]) budgetsByStates[state] = {};
            if (! budgetsByStates[state][icon]) budgetsByStates[state][icon] = { amount: 0, amount_inprogress: 0 };
            budgetsByStates[state][icon].amount += budget.amount || 0;
            budgetsByStates[state][icon].amount_inprogress += budget.amount_inprogress || 0;
          });
          acc[el.id] = budgetsByStates;
          return acc;
        }, {});
      },
      detailFilteredBudgetElements() {
        const titleFilter = this.detailFilters.title?.trim().toLowerCase();
        return this.filteredBudgetElements.filter((el) => {
          const title = el.getTitle() || this.$t('helpers.elDefaultTitle', { type: el.getType() });
          if (titleFilter && ! title.toLowerCase().includes(titleFilter)) return false;
          if (! this.detailFilters.states.length) return true;
          return (el.getBudgets() || []).some((budget) => {
            if (! budget.amount && ! budget.amount_inprogress) return false;
            const state = this.budgetStateKeysSet.has(budget.state) ? budget.state : '';
            return this.detailFilters.states.includes(state);
          });
        });
      },
      detailBudgetRows() {
        return this.detailFilteredBudgetElements.flatMap((el) => {
          const budgetsByStates = this.elBudgetsByStatesByEl[el.id] || {};
          return this.budgetStateKeys.filter((state) => {
            if (! Object.keys(budgetsByStates[state] || {}).length) return false;
            return ! this.detailFilters.states.length || this.detailFilters.states.includes(state);
          }).map((state) => {
            const budgetsByIcon = Object.keys(budgetsByStates[state] || {}).reduce((acc, icon) => {
              if (this.sortedBudgetIcons.includes(icon)) acc[icon] = budgetsByStates[state][icon];
              return acc;
            }, {});
            return {
              key: `${el.id}-${state}`,
              el,
              state,
              title: el.getTitle() || this.$t('helpers.elDefaultTitle', { type: el.getType() }),
              budgetsByIcon,
            };
          }).filter(row => Object.keys(row.budgetsByIcon).length);
        });
      },
      detailBudgetTotalsByIcon() {
        const totalsByIcon = this.sortedBudgetIcons.reduce((acc, icon) => {
          acc[icon] = { amount: 0, amount_inprogress: 0 };
          return acc;
        }, {});
        this.detailBudgetRows.forEach((row) => {
          this.sortedBudgetIcons.forEach((icon) => {
            if (! row.budgetsByIcon[icon]) return;
            totalsByIcon[icon].amount += row.budgetsByIcon[icon].amount || 0;
            totalsByIcon[icon].amount_inprogress += row.budgetsByIcon[icon].amount_inprogress || 0;
          });
        });
        return totalsByIcon;
      },
      budgetTarget() {
        return this.planning.projectsheet?.target_budget;
      },
    },
    created() {
      this.selectedBudgetIcon = this.sortedBudgetIcons[0] || '';
    },
  };
</script>
